// Platformer Level System for Helix 2000
// MapleStory-inspired level design with platforms and physics

import { Platform, PlatformerLevel, Position } from '../../../shared/CharacterTypes';
import { LevelFormatConverter } from '../../../shared/LevelFormatConverter';
import { MapSpawnResolver } from './MapSpawnResolver';
import { Teleporter } from '../../../shared/Teleporter';
import { GAME_CONSTANTS } from '../../constants/GameConstants';
import { IGameSystem } from '../../core/IGameSystem';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { LevelRegistry } from './LevelRegistry';
import { ILevelProvider } from '../../../shared/interfaces/ILevelProvider';
import { RegistryLevelProvider } from './RegistryLevelProvider';
import { TeleporterManager } from './TeleporterManager';
import { RopeManager } from './RopeManager';
import { gameLogger } from '../../../utils/GameLogger';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { evaluatePortalGate } from '../../../shared/logic/portalGate';
import { ErrorToastAPI } from '../../ui/components/ErrorToast';

export class MapSystem implements IGameSystem {
  public readonly id = 'MapSystem';

  /** DEBUG FLAGS */
  public static DISABLE_GROUND_SNAP: boolean = false;

  private currentLevel: PlatformerLevel | null = null;
  private registry: LevelRegistry;

  private levelProvider: ILevelProvider;
  private teleporterManager: TeleporterManager;
  private ropeManager: RopeManager;
  private unsubContextual: (() => void) | null = null;

  constructor(provider?: ILevelProvider) {
    this.registry = LevelRegistry.getInstance();
    this.levelProvider = provider || new RegistryLevelProvider();

    this.teleporterManager = TeleporterManager.getInstance();
    this.ropeManager = RopeManager.getInstance();

    this.unsubContextual = EventBus.getInstance().on('ui:contextualInteraction:activate', (data: { id: string }) => {
      const tp = this.getTeleporterById(data?.id);
      if (tp) {
        const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
        const player = entitySystem?.getPlayerManager()?.getPlayer();
        this.activateTeleporter(tp, player, player?.level);
      }
    });
  }

  /**
   * Initialize the system
   */
  public async initialize(config?: any): Promise<void> {
    gameLogger.info('map', '[MapSystem] Initializing with config:', config);

    const levelToLoad = config?.defaultMap || GAME_CONSTANTS.DEFAULTS.MAP;

    // Load initial level
    await this.loadLevel(levelToLoad);

    // Refresh map list from API in the background if supported
    if (this.levelProvider.fetchAvailableMaps) {
      this.levelProvider.fetchAvailableMaps().catch(e =>
        gameLogger.warn('map', '[MapSystem] Background map refresh failed:', e)
      );
    }
  }

  /**
   * Update (IGameSystem implementation)
   */
  public update(deltaTime: number, _time: number): void {
    this.updateMovingPlatforms(deltaTime);
  }

  public dispose(): void {
    if (this.unsubContextual) {
      this.unsubContextual();
      this.unsubContextual = null;
    }
    gameLogger.info('map', '[MapSystem] Disposed');
  }

  /**
   * Get list of all available levels (built-in + custom saved)
   */
  public getAvailableLevels(): import('../../../shared/interfaces/ILevelProvider').SavedMapInfo[] {
    return this.levelProvider.getAvailableMaps();
  }

  /**
   * Save current level to storage
   */
  public saveLevelToStorage(): boolean {
    if (this.currentLevel) {
      return this.levelProvider.saveLevel(this.currentLevel) !== null;
    }
    return false;
  }

  /**
   * Load a level by ID
   */
  public async loadLevel(levelId: string): Promise<PlatformerLevel> {
    gameLogger.info('map', `[MapSystem] Loading level: ${levelId}`);

    // Clear existing teleporters
    this.teleporterManager.clearTeleporters();

    // 1. Try Registry (Built-in Maps)
    let level = this.registry.getLevel(levelId);

    if (level) {
      this.currentLevel = level;
    }

    // 2. Try Provider (Local Storage / API / Registry)
    if (!level) {
      gameLogger.info('map', `[MapSystem] Level ${levelId} not in built-in Registry. Trying dynamic load via Provider...`);
      const dynamicLevel = await this.levelProvider.loadLevel(levelId);
      if (dynamicLevel) {
        gameLogger.info('map', `[MapSystem] Loaded dynamic level: ${dynamicLevel.name} with ${dynamicLevel.entities?.length || 0} entities`);
        this.currentLevel = dynamicLevel;
        level = dynamicLevel;
      }
    }

    if (level) {
      // Auto-convert GridLevel → PlatformerLevel if needed
      if (LevelFormatConverter.isGridLevel(level) && (!level.platforms || level.platforms.length === 0)) {
        gameLogger.info('map', `[MapSystem] Auto-converting GridLevel → PlatformerLevel: ${level.id}`);
        const converted = LevelFormatConverter.gridToPlatformer(level as any);
        // Merge converted platforms into the level object
        level.platforms = converted.platforms;
        level.width = converted.width; // Set pixel width
        level.height = converted.height; // Set pixel height
        (level as any).widthTiles = (level as any).widthTiles || (level as any).width;
        (level as any).heightTiles = (level as any).heightTiles || (level as any).height;
        level.spawnPoints = converted.spawnPoints;
        level.entities = converted.entities;
        level.editorGrid = level as any; // Preserve the original GridLevel for editor round-trip
        this.currentLevel = level;
      }

      // Extract entities from GridLevel entity layer if not already at top level
      if (!level.entities || level.entities.length === 0) {
        const entityLayer = (level as any).layers?.find((l: any) => l.entities?.length > 0);
        if (entityLayer) {
          level.entities = entityLayer.entities;
        }
      }

      gameLogger.info('map', `[MapSystem] Switching to ${level.name} (${level.id})`);

      // Emit level loaded event for UI updates.
      // Sticky because MapSystem boots before the React UI binds its listener;
      // the first load fires before useGameEvents is mounted. Sticky replay
      // ensures the late-binding subscriber still gets the initial level.
      EventBus.getInstance().emitSticky(EVENTS.LEVEL_LOADED, { name: level.name, id: level.id, width: level.width, height: level.height });

      // Validate level in development mode
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        try {
          const { LevelValidator } = await import('../../utils/level/LevelValidator');
          LevelValidator.validateAndLog(level);
        } catch (e) {
          gameLogger.warn('map', '[MapSystem] Could not load LevelValidator:', e);
        }
      }

      this.teleporterManager.loadTeleporters(levelId, level);
      this.ropeManager.loadRopes(level);
      return level;
    }

    gameLogger.warn('map', `[MapSystem] Level not found anywhere: ${levelId}`);

    // Fallback to starter-map if current load failed
    if (levelId !== 'starter-map') {
      gameLogger.info('map', '🔄 [MapSystem] Falling back to starter-map...');
      return this.loadLevel('starter-map');
    }

    throw new Error(`Level '${levelId}' not found and no fallback available.`);
  }

  public calculateSpawnPosition(savedPosition: Position, spawnId?: string): Position {
    return this.spawnResolver().calculateSpawnPosition(savedPosition, spawnId);
  }

  public getDefaultSpawnPosition(): Position {
    return this.spawnResolver().getDefaultSpawnPosition();
  }

  /**
   * Set level data directly (used by Editor)
   */
  public setLevelData(level: PlatformerLevel): void {
    this.currentLevel = level;
  }

  /**
   * L3.1 — graveyard picker support.
   *
   * `getSpawnPoints` exposes the current map's spawn point list (or
   * empty array when the map declares none). Snapshot — caller should
   * not mutate. Used by `CombatHandler.handlePlayerDefeated` to ship
   * the picker buttons to the client in `playerDied` payloads, and by
   * future "spawn-preview" UI surfaces.
   *
   * `getSpawnPointAt` returns a ground-validated position for a chosen
   * index, or `null` when the index is out of range — caller falls
   * back to the random default. validateOnGround keeps the picker
   * safe even if a map declares an aerial spawn coord (raycasts down
   * to the nearest platform like every other spawn path).
   */
  public getSpawnPoints(): ReadonlyArray<Position> {
    return this.currentLevel?.spawnPoints ?? [];
  }

  public getSpawnPointAt(index: number): Position | null {
    const points = this.currentLevel?.spawnPoints;
    if (!points || index < 0 || index >= points.length) return null;
    const maxFallPx = (this.currentLevel as any)?.metadata?.spawnSnapMaxFallPx;
    return this.validateOnGround(
      points[index],
      typeof maxFallPx === 'number' ? { maxFallPx } : undefined,
    );
  }

  public validateOnGround(pos: Position, opts?: { maxFallPx?: number }): Position {
    return this.spawnResolver().validateOnGround(pos, opts);
  }

  private spawnResolver(): MapSpawnResolver {
    return new MapSpawnResolver(this.currentLevel, this.teleporterManager?.getTeleporters?.() ?? []);
  }

  /**
   * Update moving platforms logic
   */
  public updateMovingPlatforms(deltaTime: number): void {
    if (!this.currentLevel) return;

    this.currentLevel.platforms.forEach((platform: Platform) => {
      if (platform.type === 'moving' && platform.velocity) {
        // Update platform position
        platform.x += platform.velocity.x * deltaTime;
        platform.y += platform.velocity.y * deltaTime;

        // Check boundaries and reverse direction
        if (platform.movementBounds) {
          if (platform.x <= platform.movementBounds.minX ||
            platform.x >= platform.movementBounds.maxX) {
            platform.velocity.x *= -1;
          }
          if (platform.y <= platform.movementBounds.minY ||
            platform.y >= platform.movementBounds.maxY) {
            platform.velocity.y *= -1;
          }
        }
      }
    });
  }

  /**
   * Get teleporter at a specific position
   */
  public getTeleporterAt(x: number, y: number): Teleporter | null {
    return this.teleporterManager.getTeleporterAt(x, y);
  }

  /**
   * Get teleporter by ID
   */
  public getTeleporterById(id: string): Teleporter | undefined {
    return this.teleporterManager.getTeleporters().find(t => t.id === id);
  }

  /**
   * Get current level
   */
  public getCurrentLevel(): PlatformerLevel | null {
    return this.currentLevel;
  }

  /**
   * Get platforms for physics collision
   */
  public getPlatforms(): Platform[] {
    return this.currentLevel?.platforms || [];
  }

  /**
   * Get teleporters
   */
  public getTeleporters(): Teleporter[] {
    return this.teleporterManager.getTeleporters();
  }

  /**
   * Get teleporter entities
   */
  public getTeleporterEntities(): any[] {
    return this.teleporterManager.getTeleporterEntities();
  }

  /**
   * Get rope manager
   */
  public getRopeManager(): RopeManager {
    return this.ropeManager;
  }

  /**
   * Get rope entities
   */
  public getRopeEntities(): any[] {
    return this.ropeManager.getRopeEntities();
  }

  /**
   * Add platform
   */
  public addPlatform(platform: Platform): void {
    if (this.currentLevel) {
      this.currentLevel.platforms.push({ ...platform });
      gameLogger.info('map', '[MapSystem] Platform added:', platform);
    }
  }

  /**
   * Delete last platform (for undo functionality)
   */
  public deleteLastPlatform(): void {
    if (this.currentLevel) {
      const removed = this.currentLevel.platforms.pop();
      gameLogger.info('map', '[MapSystem] Platform removed:', removed);
    }
  }

  /**
   * Save level to API
   */
  public async saveLevel(): Promise<boolean> {
    if (this.currentLevel && this.levelProvider.saveLevelToApi) {
      return this.levelProvider.saveLevelToApi(this.currentLevel);
    }
    return false;
  }

  /**
   * Save to local file for development
   */
  public saveLevelToFile(): boolean {
    if (this.currentLevel && this.levelProvider.saveLevelToFile) {
      return this.levelProvider.saveLevelToFile(this.currentLevel);
    }
    return false;
  }

  /**
   * Switch to map system
   * NOTE: Emits event for GameScene to handle restart
   */
  public switchMap(mapId: string, spawnId?: string): void {
    if (this.getCurrentLevel()?.id === mapId) {
      gameLogger.info('map', `[MapSystem] Already on map: ${mapId}`);
      return;
    }

    gameLogger.info('map', `[MapSystem] Logic switch map request: ${mapId} (Spawn: ${spawnId})`);
    EventBus.getInstance().emit('requestMapSwitch', { mapId, spawnId });
  }

  /**
   * Activate a teleporter
   * Handles logic for teleportation (validating, finding destination)
   * Emits 'requestMapSwitch' event for cross-map teleports
   * Handles same-map teleports directly via PlayerManager
   *
   * `playerLevel` gates cross-map portals: when the destination zone has a
   * recommended level (or the portal declares an explicit `minLevel`) and the
   * player is below it, the warp is BLOCKED and a warning toast is shown
   * instead of dumping a fresh character into a high-level dungeon. Same-map
   * teleports (e.g. intra-map shortcuts) are never gated. Pass `undefined` to
   * treat the player conservatively as level 1.
   */
  public activateTeleporter(teleporter: Teleporter, playerEntity: any, playerLevel?: number, peaceful?: boolean): void {
    if (!teleporter) {
      gameLogger.warn('map', '[MapSystem] activateTeleporter called with null teleporter');
      return;
    }

    gameLogger.info('map', `[MapSystem] Activating teleporter: ${teleporter.id}`, {
      targetMapId: teleporter.targetMapId,
      targetSpawnId: teleporter.targetSpawnId,
      targetX: teleporter.targetX,
      targetY: teleporter.targetY
    });

    if (!this.currentLevel) {
      gameLogger.error('map', '[MapSystem] Cannot teleport - no current level');
      return;
    }

    // Determine if this is a same-map or cross-map teleport
    const targetMapId = teleporter.targetMapId || this.currentLevel.id;
    const isSameMap = targetMapId === this.currentLevel.id;

    // Data-driven three-tier level gate (cross-map only — on-ramp design §3).
    // Soft-warn is the default: an under-level player within the warn band is
    // warned but allowed through (player agency over invisible walls). Only
    // hard-gated portals (the Undercroft descent) or players far below the
    // band are blocked — always with an explanatory toast, never silently.
    if (!isSameMap) {
      const isDevBypass = peaceful === true
        || (playerEntity && (playerEntity.isNoclip === true || playerEntity.godMode === true))
        || (playerLevel !== undefined && playerLevel >= 99);

      const gate = evaluatePortalGate(
        {
          minLevel: teleporter.minLevel,
          targetMapId,
          bypassZoneGate: teleporter.bypassZoneGate,
          hardGate: teleporter.hardGate,
          softWarnBand: teleporter.softWarnBand,
        },
        playerLevel,
        isDevBypass,
      );
      if (gate.gate === 'block') {
        gameLogger.info(
          'map',
          `[MapSystem] Portal ${teleporter.id} -> ${targetMapId} BLOCKED: player level ${playerLevel ?? 1} < required ${gate.requiredLevel} (${gate.source})`,
        );
        ErrorToastAPI.warn(`The fog is too thick to pass without more experience. Reach level ${gate.requiredLevel}.`);
        EventBus.getInstance().emit(EVENTS.PORTAL_BLOCKED, {
          teleporterId: teleporter.id,
          targetMapId,
          requiredLevel: gate.requiredLevel,
          playerLevel: playerLevel ?? 1,
          source: gate.source,
        });
        return;
      }
      if (gate.gate === 'warn') {
        // Portal WORKS — soft warning rides along with the warp.
        gameLogger.info(
          'map',
          `[MapSystem] Portal ${teleporter.id} -> ${targetMapId} soft-warn: player level ${playerLevel ?? 1} < recommended ${gate.requiredLevel} (${gate.source})`,
        );
        ErrorToastAPI.warn(`You should be level ${gate.requiredLevel} before venturing inland. Come back when you're ready — the fog is thick.`);
      }
    }

    const serverRoom = ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom();
    if (isSameMap && !serverRoom) {
      // Offline same-map teleport: Find target teleporter or use coordinates.
      this.handleSameMapTeleport(teleporter, playerEntity);
    } else {
      // Connected same-map portals also need a receipt: a local jump would
      // violate server movement bounds. SceneEventManager owns navigation.
      this.handleCrossMapTeleport(teleporter);
    }
  }

  /**
   * Handle teleportation within the same map
   */
  private handleSameMapTeleport(teleporter: Teleporter, playerEntity: any): void {
    let targetX = teleporter.targetX;
    let targetY = teleporter.targetY;

    // If target coordinates not specified, try to find target teleporter
    if ((targetX === undefined || targetY === undefined) && teleporter.targetSpawnId) {
      const targetTeleporter = this.teleporterManager.getTeleporters()
        .find(t => t.id === teleporter.targetSpawnId);

      if (targetTeleporter) {
        targetX = targetTeleporter.x;
        targetY = targetTeleporter.y;
        gameLogger.info('map', `[MapSystem] Found target teleporter: ${targetTeleporter.id} at (${targetX}, ${targetY})`);
      }
    }

    if (targetX === undefined || targetY === undefined) {
      gameLogger.error('map', `[MapSystem] Same-map teleport failed - no target coordinates for teleporter: ${teleporter.id}`);
      return;
    }

    // Validate position is on ground
    const targetPos = this.validateOnGround({ x: targetX, y: targetY });

    // Play teleport animation if playerEntity has methods
    if (playerEntity && typeof playerEntity.setAlpha === 'function') {
      // Fade out
      playerEntity.setAlpha(0);
      // Fade in after a short delay
      setTimeout(() => {
        if (playerEntity && typeof playerEntity.setAlpha === 'function') {
          playerEntity.setAlpha(1);
        }
      }, 200);
    }

    // Emit teleport event for EntitySystem/PlayerManager to handle
    // Using 'playerTeleport' event which is distinct from 'player:teleported' (past tense)
    EventBus.getInstance().emit(EVENTS.PLAYER_TELEPORT, {
      x: targetPos.x,
      y: targetPos.y,
      sourceTeleporterId: teleporter.id
    });

    gameLogger.info('map', `[MapSystem] Same-map teleport: (${teleporter.x}, ${teleporter.y}) -> (${targetPos.x}, ${targetPos.y})`);
  }

  /**
   * Handle cross-map teleportation
   */
  private handleCrossMapTeleport(teleporter: Teleporter): void {
    const targetMapId = teleporter.targetMapId;
    const targetSpawnId = teleporter.targetSpawnId;

    if (!targetMapId) {
      gameLogger.error('map', `[MapSystem] Cross-map teleport failed - no targetMapId for teleporter: ${teleporter.id}`);
      return;
    }

    gameLogger.info('map', `[MapSystem] Cross-map teleport: -> ${targetMapId} (spawn: ${targetSpawnId || 'default'})`);

    // Emit the switch intent; SceneEventManager turns it into a clean page
    // NAVIGATION to the target map (see its requestMapSwitch handler). The old
    // in-place scene restart made the client re-join → server kicked the
    // original session as a duplicate ("stale session", close 4000) →
    // "Connection Lost". Navigation = one fresh join, no duplicate.
    EventBus.getInstance().emit('requestMapSwitch', {
      mapId: targetMapId,
      spawnId: targetSpawnId,
      portalId: teleporter.id,
    });
  }

  /**
   * Set spawn point for current level
   */
  public setSpawnPoint(spawnIndex: number, x: number, y: number): void {
    if (!this.currentLevel) return;

    if (!this.currentLevel.spawnPoints) {
      this.currentLevel.spawnPoints = [];
    }

    if (spawnIndex >= 0 && spawnIndex < this.currentLevel.spawnPoints.length) {
      this.currentLevel.spawnPoints[spawnIndex] = { x, y };
    } else {
      this.currentLevel.spawnPoints.push({ x, y });
    }
    gameLogger.info('map', `[MapSystem] Set spawn point ${spawnIndex} to (${x}, ${y})`);
  }

  /**
   * Clear all custom platforms
   */
  public clearPlatforms(): void {
    if (!this.currentLevel) return;

    this.currentLevel.platforms = [];
    gameLogger.info('map', '[MapSystem] All platforms cleared');
  }

  /**
   * Listen for level updates (stub for now)
   */
  public listenForLevelUpdates(): void {
    gameLogger.info('map', '[MapSystem] Listening for level updates...');
  }
}
